import Dexie, { type Table } from 'dexie'
import { maybeGzipJson, gunzipJson } from './gzip'

// ─────────────────────────────────────────────────────────────────────────
// IndexedDB cache layer for bskyhq.
// All persistence is browser-local. NO Cloudflare KV / D1 / R2 / Durable
// Objects are used anywhere in this app. The worker is stateless.
// v8+: large value payloads are gzip-compressed into `z` on write; `value`
// is absent on those rows. Reads inflate `z` back to `value` transparently.
// ─────────────────────────────────────────────────────────────────────────

export interface CachedRecordRow {
  handle: string
  collection: string
  rkey: string
  cid: string | null
  uri: string
  /** Uncompressed value. Absent when `z` is set. */
  value: Record<string, unknown>
  /** gzipped JSON of `value` — set in place of `value` when compression wins. */
  z?: Uint8Array
  fetched_at: number
}

export interface CachedPostRow {
  uri: string
  cid: string | null
  did: string
  rkey: string
  /** Uncompressed value. Absent when `z` is set. */
  value: Record<string, unknown>
  /** gzipped JSON of `value` — set in place of `value` when compression wins. */
  z?: Uint8Array
  fetched_at: number
}

export interface CachedProfileRow {
  did: string
  handle: string
  uri: string
  profile: Record<string, unknown> | null
  fetched_at: number
}

/**
 * A user-staged action (like / repost / follow) that should run either now
 * or at a future time. Created by the schedule-or-now dialog. The runner
 * picks up every row whose runAt <= Date.now() and status === 'pending'.
 */
export type ScheduledKind = 'like' | 'repost' | 'follow' | 'unfollow' | 'post' | 'reply'

export interface ScheduledActionRow {
  id: string
  /** DID of the account that will perform the action. */
  accountDid: string
  kind: ScheduledKind
  /** at://… of the post for like / repost / reply parent; DID for follow.
   *  For 'post' (a fresh top-level post) this is "" — the row's identity
   *  is its id. */
  subject: string
  /** Cid of the subject post (required for like / repost / reply parent). */
  subjectCid?: string | null
  /** Body text for kind = 'post' or 'reply'. */
  text?: string
  /** For 'reply': the root and parent URIs (parent === subject). */
  replyParentUri?: string
  replyParentCid?: string
  replyRootUri?: string
  replyRootCid?: string
  /** scheduled_media row ids attached to a post / reply. The runner reads
   *  each, calls com.atproto.repo.uploadBlob, and folds the resulting
   *  blob refs into the post embed at fire time. */
  mediaIds?: string[]
  /** Unix ms when the action should fire. Date.now() for "do it now". */
  runAt: number
  /** Optional human label (e.g. "auto-follow batch 2026-05-04"). */
  label?: string
  /** "pending" | "running" | "done" | "failed" | "cancelled". */
  status: 'pending' | 'running' | 'done' | 'failed' | 'cancelled'
  /** Resulting record URI on success. */
  resultUri?: string
  /** Reason on failure (e.g. "subject post deleted"). */
  failureReason?: string
  /** Set when this action was queued by an auto-follow campaign (or
   *  any future recurring campaign concept). Lets the campaign
   *  reconcile per-target outcomes without walking the whole
   *  scheduled_actions table by hand. */
  campaignId?: string
  createdAt: number
  updatedAt: number
}

/**
 * A queued media blob attached to a scheduled post / reply. Stored in
 * IndexedDB as a real Blob (Dexie persists Blob bytes, so this survives
 * tab restart). Uploaded to the user's PDS via uploadBlob the moment the
 * owning scheduled action fires.
 */
export interface ScheduledMediaRow {
  id: string
  accountDid: string
  /** "image/jpeg" / "image/png" / "video/mp4" / etc. */
  mime: string
  /** Optional alt text for accessibility (images only). */
  alt?: string
  /** Width / height for image embeds; undefined for video where we let
   *  the PDS infer. */
  aspectRatio?: { width: number; height: number }
  /** Bytes. */
  blob: Blob
  size: number
  createdAt: number
}

/**
 * Auto-follow campaign config. The runner expands a campaign into
 * individual scheduled_actions over time according to cadenceMs.
 */
export interface AutoFollowCampaignRow {
  id: string
  accountDid: string
  /** Free label so the list can render something other than the tag
   *  string when the operator names the campaign. */
  name?: string
  /** Hashtags or keywords used for actor discovery. */
  tags: string[]
  /** Tags that would block a candidate from being followed. */
  denyTags: string[]
  /** Total follows the campaign should reach. */
  totalTarget: number
  /** Min ms between scheduled follows (throttling — anti-spam). */
  cadenceMs: number
  /** Extra random ms added on top of cadenceMs each follow so the
   *  spacing looks human. 0 to disable. */
  jitterMs?: number
  /** "running" | "paused" | "done". */
  status: 'running' | 'paused' | 'done'
  scheduledSoFar: number
  succeededSoFar: number
  failedSoFar: number
  /** When the runner is next allowed to queue another batch for this
   *  campaign. The runner sets this to lastQueueTime + cadenceMs so
   *  even if the scheduler tick fires often, no campaign queues
   *  faster than its cadence. */
  nextTickAt?: number
  /** Unix ms of the most recent runner tick — even when it did
   *  nothing, so the operator can tell the runner IS alive. */
  lastTickAt?: number
  /** One-line plain-English summary of what the most recent tick
   *  did. Rendered in the editor so problems are visible without
   *  digging through the failure log. */
  lastTickMessage?: string
  createdAt: number
  updatedAt: number
}

/**
 * One row per (handle, collection) tracking the last incremental pull.
 * Lets us answer "is there new stuff?" cheaply without re-walking the
 * whole repo. autoSync reads this on profile load to decide whether to
 * fire another pull (cooldown gate).
 */
export interface PullMetaRow {
  /** Composite id "<handle>|<collection>" — also the table primary key. */
  id: string
  handle: string
  collection: string
  /** Unix ms when the last pullCollection() finished. */
  lastPulledAt: number
  /** Newest rkey we have for this (handle, collection). */
  lastPulledRkey: string | null
  /** Number of NEW rows the last incremental pull produced. */
  lastDelta: number
  /** Best-effort known total when available (e.g. profile.postsCount /
   *  followsCount). null when the lexicon doesn't expose a total. */
  lastKnownTotal: number | null
}

/**
 * Per-(handle, collection) completeness manifest. The presence of this
 * row + completed=true is our authoritative answer to "have we actually
 * walked to cursor-end at least once?" — pull_meta only tells us we
 * pulled SOMETHING. Used by the gallery's "🔍 cache fully" path and by
 * autoSync to avoid claiming a cache is complete when it isn't.
 */
export interface PullManifestRow {
  /** Composite id "<handle>|<collection>" — primary key. */
  id: string
  handle: string
  collection: string
  /** Unix ms when the last full walk (pullCollection full=true) ended. */
  lastFullWalkAt: number | null
  /** True only after a full walk reached cursor-end without abort/error. */
  completed: boolean
  /** Best-known external total (e.g. profile.postsCount). null when the
   *  lexicon doesn't expose one. */
  totalKnown: number | null
  /** Cache count snapshot at the end of the last full walk. */
  totalCached: number
  /** Number of records discovered MISSING during the last full-walk
   *  diff (cached rkeys minus seen rkeys). Each is moved into
   *  deleted_records. */
  deletedThisWalk: number
  /** When `completed=false` and a walk was interrupted (tab close,
   *  network blip, user ✕ skip, full-cache abort) this is the last
   *  cursor we successfully consumed. The next run picks up from here
   *  instead of re-walking from the top. Cleared after a clean
   *  cursor-end completion. */
  lastCursor?: string | null
  lastCursorAt?: number | null
}

/**
 * "Likes of likes" — one row per (targetHandle, postUri, likerDid).
 * For a given target user, captures every external actor who liked
 * one of their posts/reposts/replies, the timestamp of the like, and
 * which source category the liked content fell into. Cross-section
 * queries (likers of posts AND likers of replies) read these rows
 * directly.
 */
export type LikerSourceKind = 'posts' | 'reposts' | 'replies'

export interface LikerRecordRow {
  /** Composite id "<targetHandle>|<postUri>|<likerDid>". */
  id: string
  targetHandle: string
  postUri: string
  /** Which of the target's content categories this liked post belongs to. */
  sourceKind: LikerSourceKind
  likerDid: string
  likerHandle: string
  likerDisplayName?: string
  likerAvatar?: string
  /** ISO of the like record's createdAt (when the actor hit ❤️). */
  likedAt: string
  /** Unix ms when we cached this row. */
  fetchedAt: number
}

/**
 * Append-only log of records that disappeared from the PDS between
 * full walks. The original record value is preserved verbatim so a
 * future UI can show "deleted post" cards from the cache.
 */
export interface DeletedRecordRow {
  /** Composite id "<handle>|<collection>|<rkey>" — primary key. */
  id: string
  handle: string
  collection: string
  rkey: string
  uri: string
  cid: string | null
  /** The frozen record value at the time of last cache. */
  value: Record<string, unknown>
  /** Unix ms when the diff first noticed it was gone. */
  noticedAt: number
}

/**
 * Append-only log of failures. The schedule view filters here. We keep
 * this separate from scheduled_actions so the action history stays a
 * scrub-friendly list while the failure log is the audit trail.
 */
export interface FailureLogRow {
  id: string
  /** What kind of operation failed — "xrpc", "scheduler", "pull",
   *  "autoSync", "campaign" etc. Free-form. Older rows (scheduler-
   *  only) leave this undefined and we coerce to "scheduler" when
   *  rendering. */
  op?: string
  /** Human-readable target — for xrpc this is the path; for
   *  scheduler it mirrors the action subject; for pull it's the
   *  handle+collection. */
  target?: string
  /** Set when this failure came from a scheduled action. */
  actionId?: string
  accountDid: string
  /** Set when actionId is set. */
  kind?: ScheduledActionRow['kind']
  /** Older rows used `subject` for the scheduled action subject —
   *  preserved for backwards compat. New rows prefer `target`. */
  subject?: string
  reason: string
  /** XRPC status code (404 / 410 etc) when known. */
  httpStatus?: number
  occurredAt: number
  /** Last time this row was bumped — equals occurredAt for one-shot
   *  rows, gets touched when an identical (op + target + status)
   *  failure recurs within 60s and we coalesce instead of inserting
   *  a fresh row. */
  lastSeenAt?: number
  /** Number of times this same failure was observed (1 = the
   *  original). Bumped by the coalescing logic in logFailure(). */
  count?: number
  /** Stable identity used to coalesce recurrences into this one row
   *  (op + target + httpStatus + accountDid). Indexed so a recurring
   *  failure finds its existing row in O(log n) instead of scanning. */
  dedupKey?: string
  /** Epoch ms of each individual occurrence, most-recent last. Capped
   *  (see OCCURRENCE_CAP) so a runaway loop can't grow one row without
   *  bound — `count` stays exact even after the array saturates. Drives
   *  the failure-log's expandable "when did this happen" view. */
  times?: number[]
  /** Distinct context strings sampled across occurrences (e.g. the
   *  specific failing image URLs / post URIs). Capped (SAMPLE_CAP). */
  samples?: string[]
}

/**
 * Persisted horny-meter aggregates keyed by handle. computeStatsFor()
 * walks the cached likes once and writes this row so re-renders +
 * AI calls are instant (no re-iteration over thousands of likes).
 *
 * `version` lets us bump computation logic without invalidating the
 * Dexie schema — bump it and bumped rows are treated as stale.
 */
export interface HornyStatsRow {
  handle: string
  computedAt: number
  /** Algorithm version. Bump when scoring changes so cached rows
   *  recompute on next view. */
  version: number
  totalLikes: number
  mediaLikes: number
  byLabel: number
  byTag: number
  adultMediaLikes: number
  pct: number
  daysSpanned: number
  perDay: number
  minutesPerDay: number
  oldestMs: number | null
  newestMs: number | null
  topAdultTags: { tag: string; count: number }[]
  topAllTags: { tag: string; count: number }[]
  /** Full per-tag count map for the AI to chew on. */
  allTagCounts: Record<string, number>
}

/** A recurring AI campaign that pulls posts from a Bsky search, asks
 *  a vision-capable agent to score each image, and acts on each post
 *  based on the score (≥ repostThreshold → repost, in likeRange →
 *  like, otherwise skip). When dryRun is true the runner logs the
 *  scores + intended actions WITHOUT touching the network — used by
 *  the validator button and by brand-new campaigns until you flip
 *  the switch. */
export interface AiCampaignRow {
  id: string
  accountDid: string
  name: string
  query: string
  agentId: string
  /** Inclusive range. score ∈ [likeMin, likeMax] → like the post. */
  likeMin: number
  likeMax: number
  /** score &gt; repostThreshold → repost. */
  repostThreshold: number
  /** Every post URI this campaign has ever evaluated. Stored directly on
   *  the campaign row so dedup is instant and survives page reloads. */
  seenPostUris?: string[]
  /** Last pagination cursor per combo query — advanced each run so we don't
   *  re-pull the same top-N posts every time. Key = the combo query string. */
  searchCursors?: Record<string, string>
  imagesPerRun: number
  intervalMin: number
  /** @deprecated use runIntervalMin / runIntervalMax */
  jitterMin: number
  /** Minimum minutes between runs. Supersedes intervalMin. Default 60. */
  runIntervalMin?: number
  /** Maximum minutes between runs. Actual wait = random(runIntervalMin, runIntervalMax). Default 120. */
  runIntervalMax?: number
  enabled: boolean
  dryRun: boolean
  /** Quality scores at or below this threshold are rejected outright —
   *  bad art never earns likes from lewdness alone. Default 3. */
  scoreQualityGate: number
  /** Weight applied to quality in final score (0–1). Lewdness weight
   *  = 1 − scoreQualityWeight. Default 0.45. */
  scoreQualityWeight: number
  /** When true, photorealistic photos are hard-rejected (score = 0).
   *  Default true. */
  scorePhotoGate: boolean
  /** Maximum times a single author handle can be liked/reposted across
   *  the last `handleWindowRuns` runs. Prevents the campaign from
   *  repeatedly engaging the same small pool of accounts. Default 2. */
  maxPostsPerHandle: number
  /** How many recent runs to scan when counting per-handle likes.
   *  Set to 0 to only enforce the cap within a single run. Default 10. */
  handleWindowRuns: number
  /** 'image' (default): score quality + lewdness via vision.
   *  'text': score poetryQuality + originality via text. */
  scoringMode?: 'image' | 'text'
  /** Weight for poetryQuality in text-mode score (0–1). Originality = 1 − this. Default 0.5. */
  scorePoetryQualityWeight?: number
  /** Text-mode gate: poetryQuality ≤ this → score = poetryQuality (originality adds nothing). Default 3. */
  scorePoetryGate?: number
  /** Minimum sleep in seconds between each like / repost / follow call. Default 1. */
  actionSleepMin?: number
  /** Maximum sleep in seconds between actions. Actual sleep = random(actionSleepMin, actionSleepMax). Default 4. */
  actionSleepMax?: number
  /** @deprecated use actionSleepMax */
  actionSleepJitter?: number
  /** Earliest hour (0–23, local time) at which this campaign may fire. Undefined = no restriction. */
  runHourStart?: number
  /** Latest hour (0–23, local time, inclusive) at which this campaign may fire. Undefined = no restriction. */
  runHourEnd?: number
  nextRunAt: number
  lastRunAt?: number
  /** Error message from the most recent run, if it failed. Cleared
   *  on the next successful run. Shown on the campaign list card so
   *  the operator sees failing campaigns at a glance. */
  lastError?: string
  createdAt: number
  updatedAt: number
}

/** A single execution of an AI campaign — used by the campaigns UI to
 *  show history and by the validator to display the most recent
 *  dry-run output. */
export interface AiCampaignRunRow {
  id: string
  campaignId: string
  startedAt: number
  finishedAt?: number
  error?: string
  dryRun: boolean
  evaluations: {
    postUri: string
    authorHandle: string
    imageUrl: string
    score: number
    /** Raw quality score (0-10) from the agent, before formula is applied. Image mode only. */
    quality?: number
    /** Raw lewdness score (0-10) from the agent, before formula is applied. Image mode only. */
    lewdness?: number
    /** Raw poetry-quality score (0-10). Text mode only. */
    poetryQuality?: number
    /** Raw originality score (0-10). Text mode only. */
    originality?: number
    reason: string
    action: 'none' | 'like' | 'repost'
    /** Set after execution: true = record created, false = call threw. */
    executed?: boolean
    /** Error message when executed=false. */
    executeError?: string
  }[]
  /** Stored raw response text from the agent — useful for debugging
   *  prompt drift. */
  rawAgentText?: string
  /** Stats from the dedup pass — shows whether seenPostUris accumulation
   *  is working. Populated on every run since v1.10.36. */
  dedupStats?: {
    seenUrisBefore: number
    rawImageHits: number
    passedDedup: number
    /** How many posts were excluded by the handle-diversity filter. */
    handleFiltered?: number
  }
}

class BskyHqDB extends Dexie {
  records!: Table<CachedRecordRow, [string, string, string]>
  posts!: Table<CachedPostRow, string>
  profiles!: Table<CachedProfileRow, string>
  scheduled_actions!: Table<ScheduledActionRow, string>
  scheduled_media!: Table<ScheduledMediaRow, string>
  pull_meta!: Table<PullMetaRow, string>
  pull_manifest!: Table<PullManifestRow, string>
  deleted_records!: Table<DeletedRecordRow, string>
  liker_records!: Table<LikerRecordRow, string>
  auto_follow_campaigns!: Table<AutoFollowCampaignRow, string>
  failure_log!: Table<FailureLogRow, string>
  horny_stats!: Table<HornyStatsRow, string>
  ai_campaigns!: Table<AiCampaignRow, string>
  ai_campaign_runs!: Table<AiCampaignRunRow, string>
  kv!: Table<KvRow, string>

  constructor() {
    super('bskyhq')
    this.version(1).stores({
      records:
        '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      scheduled_actions:
        'id, accountDid, [accountDid+status], status, runAt, kind, createdAt',
      auto_follow_campaigns: 'id, accountDid, status, createdAt',
      failure_log: 'id, accountDid, actionId, occurredAt',
    })
    // v2 — adds compose support: post/reply kinds carry text + media; new
    // scheduled_media table holds the Blob bytes. No data migration needed
    // for existing rows: a like/repost/follow action without text+mediaIds
    // is still valid.
    this.version(2).stores({
      records:
        '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      scheduled_actions:
        'id, accountDid, [accountDid+status], status, runAt, kind, createdAt',
      scheduled_media: 'id, accountDid, createdAt',
      auto_follow_campaigns: 'id, accountDid, status, createdAt',
      failure_log: 'id, accountDid, actionId, occurredAt',
    })
    // v3 — pull_meta table for auto-sync cooldown + delta telemetry.
    this.version(3).stores({
      records:
        '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      scheduled_actions:
        'id, accountDid, [accountDid+status], status, runAt, kind, createdAt',
      scheduled_media: 'id, accountDid, createdAt',
      pull_meta: 'id, [handle+collection], handle, collection, lastPulledAt',
      auto_follow_campaigns: 'id, accountDid, status, createdAt',
      failure_log: 'id, accountDid, actionId, occurredAt',
    })
    // v4 — pull_manifest (completeness signal) + deleted_records (the
    // append-only "this disappeared from the PDS" log). Both are part
    // of phase-10 cache-completeness work.
    this.version(4).stores({
      records:
        '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      scheduled_actions:
        'id, accountDid, [accountDid+status], status, runAt, kind, createdAt',
      scheduled_media: 'id, accountDid, createdAt',
      pull_meta: 'id, [handle+collection], handle, collection, lastPulledAt',
      pull_manifest:
        'id, [handle+collection], handle, collection, lastFullWalkAt, completed',
      deleted_records:
        'id, [handle+collection], handle, collection, rkey, noticedAt',
      auto_follow_campaigns: 'id, accountDid, status, createdAt',
      failure_log: 'id, accountDid, actionId, occurredAt',
    })
    // v5 — liker_records ("likes of likes" — actors who liked the
    // target's posts / reposts / replies). Phase 11.
    this.version(5).stores({
      records:
        '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      scheduled_actions:
        'id, accountDid, [accountDid+status], status, runAt, kind, createdAt',
      scheduled_media: 'id, accountDid, createdAt',
      pull_meta: 'id, [handle+collection], handle, collection, lastPulledAt',
      pull_manifest:
        'id, [handle+collection], handle, collection, lastFullWalkAt, completed',
      deleted_records:
        'id, [handle+collection], handle, collection, rkey, noticedAt',
      liker_records:
        'id, targetHandle, [targetHandle+sourceKind], [targetHandle+postUri], [targetHandle+likerDid], likerDid, likedAt, fetchedAt',
      auto_follow_campaigns: 'id, accountDid, status, createdAt',
      failure_log: 'id, accountDid, actionId, occurredAt',
    })
    // v6 — horny_stats: persisted aggregates per handle so the
    // HornyMeter can render instantly from cache + recompute in the
    // background, and the AI agent can be handed the JSON directly.
    this.version(6).stores({
      records:
        '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      scheduled_actions:
        'id, accountDid, [accountDid+status], status, runAt, kind, createdAt',
      scheduled_media: 'id, accountDid, createdAt',
      pull_meta: 'id, [handle+collection], handle, collection, lastPulledAt',
      pull_manifest:
        'id, [handle+collection], handle, collection, lastFullWalkAt, completed',
      deleted_records:
        'id, [handle+collection], handle, collection, rkey, noticedAt',
      liker_records:
        'id, targetHandle, [targetHandle+sourceKind], [targetHandle+postUri], [targetHandle+likerDid], likerDid, likedAt, fetchedAt',
      auto_follow_campaigns: 'id, accountDid, status, createdAt',
      failure_log: 'id, accountDid, actionId, occurredAt',
      horny_stats: 'handle, computedAt',
    })
    // v7 — ai_campaigns: recurring vision-AI curation rules + their
    // per-run logs. The runner tick picks up due campaigns and writes
    // a run row each pass; the campaigns page lists & edits them.
    this.version(7).stores({
      records:
        '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      scheduled_actions:
        'id, accountDid, [accountDid+status], status, runAt, kind, createdAt',
      scheduled_media: 'id, accountDid, createdAt',
      pull_meta: 'id, [handle+collection], handle, collection, lastPulledAt',
      pull_manifest:
        'id, [handle+collection], handle, collection, lastFullWalkAt, completed',
      deleted_records:
        'id, [handle+collection], handle, collection, rkey, noticedAt',
      liker_records:
        'id, targetHandle, [targetHandle+sourceKind], [targetHandle+postUri], [targetHandle+likerDid], likerDid, likedAt, fetchedAt',
      auto_follow_campaigns: 'id, accountDid, status, createdAt',
      failure_log: 'id, accountDid, actionId, occurredAt',
      horny_stats: 'handle, computedAt',
      ai_campaigns: 'id, accountDid, enabled, nextRunAt, createdAt',
      ai_campaign_runs: 'id, campaignId, startedAt',
    })
    // v8 — no schema change; adds optional `z` gzip field on records + posts.
    // Old rows remain untouched; new writes compress when it helps.
    this.version(8).stores({
      records:
        '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      scheduled_actions:
        'id, accountDid, [accountDid+status], status, runAt, kind, createdAt',
      scheduled_media: 'id, accountDid, createdAt',
      pull_meta: 'id, [handle+collection], handle, collection, lastPulledAt',
      pull_manifest:
        'id, [handle+collection], handle, collection, lastFullWalkAt, completed',
      deleted_records:
        'id, [handle+collection], handle, collection, rkey, noticedAt',
      liker_records:
        'id, targetHandle, [targetHandle+sourceKind], [targetHandle+postUri], [targetHandle+likerDid], likerDid, likedAt, fetchedAt',
      auto_follow_campaigns: 'id, accountDid, status, createdAt',
      failure_log: 'id, accountDid, actionId, occurredAt',
      horny_stats: 'handle, computedAt',
      ai_campaigns: 'id, accountDid, enabled, nextRunAt, createdAt',
      ai_campaign_runs: 'id, campaignId, startedAt',
    })
    // v9 — failure_log gains a `dedupKey` index so recurring failures
    // (image 404s, repeated xrpc errors) coalesce into one row + count
    // across ALL time, not just a 60s window. No migration needed: old
    // rows simply lack dedupKey and get their own new coalesced row the
    // next time that failure recurs.
    this.version(9).stores({
      records:
        '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      scheduled_actions:
        'id, accountDid, [accountDid+status], status, runAt, kind, createdAt',
      scheduled_media: 'id, accountDid, createdAt',
      pull_meta: 'id, [handle+collection], handle, collection, lastPulledAt',
      pull_manifest:
        'id, [handle+collection], handle, collection, lastFullWalkAt, completed',
      deleted_records:
        'id, [handle+collection], handle, collection, rkey, noticedAt',
      liker_records:
        'id, targetHandle, [targetHandle+sourceKind], [targetHandle+postUri], [targetHandle+likerDid], likerDid, likedAt, fetchedAt',
      auto_follow_campaigns: 'id, accountDid, status, createdAt',
      failure_log: 'id, accountDid, actionId, occurredAt, dedupKey',
      horny_stats: 'handle, computedAt',
      ai_campaigns: 'id, accountDid, enabled, nextRunAt, createdAt',
      ai_campaign_runs: 'id, campaignId, startedAt',
    })
    // v10 — indexes the hot query paths + a kv table.
    //  * scheduled_actions gains a campaignId index (campaign reconcile /
    //    reset previously walked the whole table).
    //  * ai_campaign_runs gains [campaignId+startedAt] so recent-runs
    //    queries sort+limit inside IndexedDB instead of JS.
    //  * kv: tiny key-value rows (e.g. the disk-archive directory handle —
    //    FileSystemDirectoryHandle is structured-cloneable).
    // IndexedDB re-indexes existing rows automatically on upgrade.
    this.version(10).stores({
      records:
        '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      scheduled_actions:
        'id, accountDid, [accountDid+status], status, runAt, kind, createdAt, campaignId',
      scheduled_media: 'id, accountDid, createdAt',
      pull_meta: 'id, [handle+collection], handle, collection, lastPulledAt',
      pull_manifest:
        'id, [handle+collection], handle, collection, lastFullWalkAt, completed',
      deleted_records:
        'id, [handle+collection], handle, collection, rkey, noticedAt',
      liker_records:
        'id, targetHandle, [targetHandle+sourceKind], [targetHandle+postUri], [targetHandle+likerDid], likerDid, likedAt, fetchedAt',
      auto_follow_campaigns: 'id, accountDid, status, createdAt',
      failure_log: 'id, accountDid, actionId, occurredAt, dedupKey',
      horny_stats: 'handle, computedAt',
      ai_campaigns: 'id, accountDid, enabled, nextRunAt, createdAt',
      ai_campaign_runs: 'id, campaignId, startedAt, [campaignId+startedAt]',
      kv: 'key',
    })
  }
}

/** Generic key-value row — see v10 schema note. */
export interface KvRow {
  key: string
  value: unknown
}

export const db = new BskyHqDB()

// Eagerly open so an IndexedDB failure (corrupt upgrade, blocked by another
// tab, private-mode quota, disk full) surfaces instead of silently making
// EVERY cache read + every logFailure a no-op. logFailure and listFailures
// both depend on this; when it fails, the error log looks "completely
// broken" for a reason that has nothing to do with the logging code.
let dbOpenError: string | null = null
db.open().catch((e: unknown) => {
  dbOpenError = e instanceof Error ? `${e.name}: ${e.message}` : String(e)
  // eslint-disable-next-line no-console
  console.error('[bskyhq] IndexedDB failed to open — cache + logging disabled', e)
})

/** Non-null when the IndexedDB database could not be opened. The error log
 *  page shows this so a stuck DB is diagnosable instead of just "empty". */
export function getDbOpenError(): string | null {
  return dbOpenError
}

// ─────────── records ───────────

const RECORD_RANGE = (handle: string, collection: string) =>
  ({ lower: [handle, collection, ''], upper: [handle, collection, '￿'] }) as const

export async function listCached(
  handle: string,
  collection: string,
  offset = 0,
  limit = 1000,
): Promise<CachedRecordRow[]> {
  const range = RECORD_RANGE(handle, collection)
  const rows = await db.records
    .where('[handle+collection+rkey]')
    .between(range.lower, range.upper)
    .reverse()
    .offset(offset)
    .limit(limit)
    .toArray()
  return Promise.all(rows.map(inflateRecord))
}

/** Distinct handles that have at least one cached record — i.e. every
 *  account whose repo we've pulled into IndexedDB, whether or not it's
 *  currently open as a tab. Drives the top-bar "cached accounts" search
 *  scope. Handles are stored lowercased (see pull.ts), so these are
 *  ready to feed straight into listCached(). */
export async function listCachedHandles(): Promise<string[]> {
  const keys = (await db.records.orderBy('handle').uniqueKeys()) as string[]
  return keys.filter((h) => typeof h === 'string' && h.length > 0)
}

export async function newestRkey(
  handle: string,
  collection: string,
): Promise<string | null> {
  const range = RECORD_RANGE(handle, collection)
  const r = await db.records
    .where('[handle+collection+rkey]')
    .between(range.lower, range.upper)
    .last()
  return r?.rkey ?? null
}

export async function countCached(handle: string, collection: string): Promise<number> {
  return await db.records
    .where('[handle+collection]')
    .equals([handle, collection])
    .count()
}

export async function upsertRecords(rows: CachedRecordRow[]): Promise<void> {
  if (rows.length === 0) return
  const compressed = await Promise.all(
    rows.map(async (row) => {
      const z = await maybeGzipJson(row.value)
      if (z) return { ...row, value: {} as Record<string, unknown>, z }
      return row
    }),
  )
  await db.records.bulkPut(compressed)
}

async function inflateRecord(row: CachedRecordRow): Promise<CachedRecordRow> {
  if (row.z) return { ...row, value: await gunzipJson<Record<string, unknown>>(row.z) }
  return row
}

// ─────────── posts (hydrated subjects) ───────────

export async function getCachedPost(uri: string): Promise<CachedPostRow | undefined> {
  const row = await db.posts.get(uri)
  if (!row) return undefined
  if (row.z) return { ...row, value: await gunzipJson<Record<string, unknown>>(row.z) }
  return row
}

export async function upsertPost(row: CachedPostRow): Promise<void> {
  const z = await maybeGzipJson(row.value)
  await db.posts.put(z ? { ...row, value: {} as Record<string, unknown>, z } : row)
}

// ─────────── profiles ───────────

export async function getCachedProfileByDid(
  did: string,
): Promise<CachedProfileRow | undefined> {
  return await db.profiles.get(did)
}

export async function getCachedProfileByHandle(
  handle: string,
): Promise<CachedProfileRow | undefined> {
  return await db.profiles.where('handle').equals(handle).first()
}

export async function upsertProfile(row: CachedProfileRow): Promise<void> {
  await db.profiles.put(row)
}

export async function listAllProfiles(): Promise<CachedProfileRow[]> {
  return await db.profiles.orderBy('fetched_at').reverse().toArray()
}

// ─────────── scheduled actions ───────────

export async function listScheduledActions(
  accountDid?: string,
  status?: ScheduledActionRow['status'],
): Promise<ScheduledActionRow[]> {
  let coll
  if (accountDid && status) {
    coll = db.scheduled_actions
      .where('[accountDid+status]')
      .equals([accountDid, status])
  } else if (accountDid) {
    coll = db.scheduled_actions.where('accountDid').equals(accountDid)
  } else if (status) {
    coll = db.scheduled_actions.where('status').equals(status)
  } else {
    coll = db.scheduled_actions.toCollection()
  }
  return (await coll.toArray()).sort((a, b) => b.createdAt - a.createdAt)
}

export async function dueActions(now: number): Promise<ScheduledActionRow[]> {
  return await db.scheduled_actions
    .where('status')
    .equals('pending')
    .and((r) => r.runAt <= now)
    .toArray()
}

export async function upsertScheduledAction(row: ScheduledActionRow): Promise<void> {
  await db.scheduled_actions.put(row)
}

export async function deleteScheduledAction(id: string): Promise<void> {
  await db.scheduled_actions.delete(id)
}

// ─────────── pull_manifest ───────────

export async function getManifest(
  handle: string,
  collection: string,
): Promise<PullManifestRow | undefined> {
  return await db.pull_manifest.get(`${handle}|${collection}`)
}

export async function putManifest(row: PullManifestRow): Promise<void> {
  await db.pull_manifest.put(row)
}

export async function listManifests(
  handle: string,
): Promise<PullManifestRow[]> {
  return await db.pull_manifest.where('handle').equals(handle).toArray()
}

// ─────────── deleted_records ───────────

export async function appendDeleted(rows: DeletedRecordRow[]): Promise<void> {
  if (rows.length === 0) return
  await db.deleted_records.bulkPut(rows)
}

export async function listDeleted(
  handle: string,
  collection?: string,
): Promise<DeletedRecordRow[]> {
  if (collection) {
    return await db.deleted_records
      .where('[handle+collection]')
      .equals([handle, collection])
      .toArray()
  }
  return await db.deleted_records.where('handle').equals(handle).toArray()
}

export async function deleteCachedRecords(
  handle: string,
  collection: string,
  rkeys: string[],
): Promise<void> {
  if (rkeys.length === 0) return
  await db.records.bulkDelete(rkeys.map((rkey) => [handle, collection, rkey] as [string, string, string]))
}

// ─────────── liker_records (phase 11) ───────────

// Listeners notified after every successful liker write so open views
// (sidebar list, likers content gallery) can live-refresh during a pull
// instead of only seeing new rows on their next remount.
const likerListeners = new Set<() => void>()

/** Subscribe to liker_records writes. Returns an unsubscribe fn. */
export function onLikersWritten(fn: () => void): () => void {
  likerListeners.add(fn)
  return () => {
    likerListeners.delete(fn)
  }
}

export async function bulkPutLikers(rows: LikerRecordRow[]): Promise<void> {
  if (rows.length === 0) return
  try {
    await db.liker_records.bulkPut(rows)
  } catch (e) {
    // Write rejected — almost always storage quota (the SW image cache
    // shares the origin quota). Pinned media is NEVER freed to make room
    // (it's unrecoverable once the post is deleted upstream), so log +
    // rethrow so the pull SURFACES the failure instead of silently
    // dropping rows; the failure-log page offers the manual free button.
    void logFailure({
      op: 'likersPull',
      target: rows[0]?.targetHandle ?? '?',
      reason: e instanceof Error ? e.message : String(e),
    })
    throw e
  }
  for (const l of likerListeners) {
    try {
      l()
    } catch {
      /* ignore listener errors */
    }
  }
}

export async function listLikersForTarget(
  targetHandle: string,
  sources?: LikerSourceKind[],
): Promise<LikerRecordRow[]> {
  if (sources && sources.length > 0) {
    const out: LikerRecordRow[] = []
    for (const s of sources) {
      const rows = await db.liker_records
        .where('[targetHandle+sourceKind]')
        .equals([targetHandle, s])
        .toArray()
      out.push(...rows)
    }
    return out
  }
  return await db.liker_records.where('targetHandle').equals(targetHandle).toArray()
}

export async function likerSeenSet(
  targetHandle: string,
  postUri: string,
): Promise<Set<string>> {
  const rows = await db.liker_records
    .where('[targetHandle+postUri]')
    .equals([targetHandle, postUri])
    .toArray()
  return new Set(rows.map((r) => r.likerDid))
}

// ─────────── scheduled media ───────────

export async function putMedia(row: ScheduledMediaRow): Promise<void> {
  await db.scheduled_media.put(row)
}

export async function getMedia(id: string): Promise<ScheduledMediaRow | undefined> {
  return await db.scheduled_media.get(id)
}

export async function deleteMedia(id: string): Promise<void> {
  await db.scheduled_media.delete(id)
}

/**
 * Unbounded read of every cached record for (handle, collection),
 * newest rkey first. Use this when callers must see the WHOLE cache —
 * the original listCached() caps at 1000 / 5000 which silently drops
 * the tail for big repos.
 */
export async function listAllCached(
  handle: string,
  collection: string,
): Promise<CachedRecordRow[]> {
  const range = RECORD_RANGE(handle, collection)
  return await db.records
    .where('[handle+collection+rkey]')
    .between(range.lower, range.upper)
    .reverse()
    .toArray()
}

/** Set of every rkey we have cached for (handle, collection). Cheap
 *  enough for the deletion-diff inside fullCachePull because we only
 *  pull rkeys (not whole rows). */
export async function cachedRkeySet(
  handle: string,
  collection: string,
): Promise<Set<string>> {
  const range = RECORD_RANGE(handle, collection)
  const set = new Set<string>()
  await db.records
    .where('[handle+collection+rkey]')
    .between(range.lower, range.upper)
    .each((r) => {
      set.add(r.rkey)
    })
  return set
}

// ─────────── pull_meta ───────────

export async function getPullMeta(
  handle: string,
  collection: string,
): Promise<PullMetaRow | undefined> {
  return await db.pull_meta.get(`${handle}|${collection}`)
}

export async function putPullMeta(row: PullMetaRow): Promise<void> {
  await db.pull_meta.put(row)
}

export async function listPullMeta(handle: string): Promise<PullMetaRow[]> {
  return await db.pull_meta.where('handle').equals(handle).toArray()
}

// ─────────── failure log ───────────

const FAILURE_LOG_CAP = 1000

// ── In-memory fallback log ──────────────────────────────────────────────
// IndexedDB writes can fail wholesale — storage quota exhausted (the SW
// image-pin cache shares the origin quota), a corrupt / half-upgraded DB,
// private-mode. When they do, a purely DB-backed error log shows NOTHING,
// which is exactly the "logs are completely broken" trap we kept hitting.
// So every failure is ALSO kept in this per-tab ring buffer, and
// listFailures() merges it with the DB. Errors are therefore visible the
// instant they happen, regardless of whether the write persisted.
const MEM_LOG_CAP = 500
const memFailures: FailureLogRow[] = []
const memListeners = new Set<() => void>()

function pushMem(row: FailureLogRow): void {
  memFailures.push(row)
  if (memFailures.length > MEM_LOG_CAP) {
    memFailures.splice(0, memFailures.length - MEM_LOG_CAP)
  }
  for (const l of memListeners) {
    try { l() } catch { /* ignore listener errors */ }
  }
}

/** Subscribe to in-memory failure-log changes so a UI updates the instant
 *  something is logged, without polling. Returns an unsubscribe fn. */
export function onFailureLogged(fn: () => void): () => void {
  memListeners.add(fn)
  return () => { memListeners.delete(fn) }
}

/** How many failures are held only in memory (not persisted). >0 means the
 *  DB is rejecting writes — usually a full storage quota. */
export function memFailureCount(): number {
  return memFailures.length
}

// NOTE: there is deliberately NO image-cache deletion path anywhere in the
// app anymore (the old freeImageCaches() + its /failure-log button were
// removed in v1.31.0). Pinned media is the only surviving copy of
// upstream-deleted posts; quota pressure is handled by the background
// compactor (src/cache/compactor.ts), which recompresses but never evicts.

/** Startup storage guard: ask the browser to make this origin's storage
 *  persistent (exempt from automatic browser eviction) and report usage.
 *  NEVER deletes pinned media — cached images are the only surviving copy
 *  once a post is deleted upstream, so quota pressure is handled by the
 *  background compactor (recompression, never eviction), not by silent
 *  image loss. Called from app boot. */
export async function guardStorage(): Promise<{ persisted: boolean; usage: number; quota: number } | null> {
  try {
    let persisted = false
    try {
      persisted = (await navigator.storage?.persisted?.()) ?? false
      if (!persisted) persisted = (await navigator.storage?.persist?.()) ?? false
    } catch {
      /* persistence API unsupported — estimate below still works */
    }
    const est = await navigator.storage?.estimate?.()
    return { persisted, usage: est?.usage ?? 0, quota: est?.quota ?? 0 }
  } catch {
    return null
  }
}

let recovering = false

export async function appendFailure(row: FailureLogRow): Promise<void> {
  // Always keep the row in memory first, so it's visible even if the DB
  // write below is rejected. This is what makes the error log survive a
  // full / broken database.
  pushMem(row)
  try {
    await db.failure_log.put(row)
  } catch {
    // Write rejected — almost always storage quota. Nudge the background
    // cache compactor (it recompresses, never deletes — see compactor.ts).
    // Fire-and-forget: logging must never depend on the compactor.
    try {
      window.dispatchEvent(new CustomEvent('bskyhq:quota-pressure'))
    } catch {
      /* non-window context — compactor runs on its own timers anyway */
    }
    // NEVER free pinned media to make room (a cached image is
    // unrecoverable once Bluesky deletes the blob upstream). Make room
    // from our own table instead: deletes succeed even under a full
    // quota. If the retry still fails, the in-memory copy stands in.
    if (!recovering) {
      recovering = true
      try {
        const oldest = await db.failure_log.orderBy('occurredAt').limit(100).toArray()
        if (oldest.length > 0) {
          await db.failure_log.bulkDelete(oldest.map((r) => r.id))
        }
        await db.failure_log.put(row)
      } catch {
        /* in-memory copy covers display; give up on persistence for now */
      } finally {
        recovering = false
      }
    }
    return
  }
  // FIFO cap so a long outage doesn't grow the table forever.
  try {
    const count = await db.failure_log.count()
    if (count > FAILURE_LOG_CAP) {
      const oldest = await db.failure_log
        .orderBy('occurredAt')
        .limit(count - FAILURE_LOG_CAP)
        .toArray()
      await db.failure_log.bulkDelete(oldest.map((r) => r.id))
    }
  } catch {
    /* ignore — logging should never throw to its caller */
  }
}

/** Bound on a batch row's `times` array. `count` stays exact even when
 *  the array saturates. */
const OCCURRENCE_CAP = 50

/** Drop-in convenience for any catch block. Generates an id, swallows
 *  errors so logging never affects the caller. Plain append-only: every
 *  call writes its own row with its own timestamp — no cross-call
 *  coalescing. A single call can still represent a batch of identical
 *  failures via `occurrences` / `times` (e.g. one grouped image-error
 *  flush); those rows keep the ×N timestamp expander in the log view. */
export async function logFailure(input: {
  op: string
  target: string
  accountDid?: string
  reason: string
  httpStatus?: number
  /** Number of identical occurrences this one call represents (default 1).
   *  Lets a batch (e.g. 30 dropped subjects in one hydrate) add 30 to the
   *  count without 30 separate writes. */
  occurrences?: number
  /** Explicit occurrence timestamps (epoch ms). Defaults to [now] * n. */
  times?: number[]
  /** One context string (failing URL / post URI) to fold into `samples`. */
  sample?: string
}): Promise<void> {
  try {
    const now = Date.now()
    const bump = Math.max(1, input.occurrences ?? 1)
    const stamps = input.times && input.times.length > 0
      ? [...input.times].sort((a, b) => a - b)
      : Array.from({ length: bump }, () => now)
    const capped = stamps.length > OCCURRENCE_CAP
      ? stamps.slice(stamps.length - OCCURRENCE_CAP)
      : stamps
    const row: FailureLogRow = {
      id: `f_${now.toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      op: input.op,
      target: input.target,
      accountDid: input.accountDid ?? '',
      reason: input.reason,
      httpStatus: input.httpStatus,
      occurredAt: capped[0] ?? now,
      lastSeenAt: capped[capped.length - 1] ?? now,
      count: bump,
      times: capped,
      samples: input.sample ? [input.sample] : undefined,
    }
    await appendFailure(row)
  } catch {
    /* logging should never throw to its caller */
  }
}

export async function listFailures(accountDid?: string): Promise<FailureLogRow[]> {
  // Merge the persisted rows with the in-memory fallback so the log shows
  // entries even when the DB is unreadable / unwritable. DB rows win over
  // their in-memory twin (same id) since they carry any later mutations.
  let dbRows: FailureLogRow[] = []
  try {
    dbRows = accountDid
      ? await db.failure_log.where('accountDid').equals(accountDid).toArray()
      : await db.failure_log.toArray()
  } catch {
    /* DB unreadable — fall back to the in-memory log only */
  }
  const byId = new Map<string, FailureLogRow>()
  for (const r of memFailures) {
    if (!accountDid || (r.accountDid ?? '') === accountDid) byId.set(r.id, r)
  }
  for (const r of dbRows) byId.set(r.id, r)
  // Sort by most-recent activity so a failure that just recurred bubbles
  // to the top even though its row (and occurredAt) is old.
  return [...byId.values()].sort(
    (a, b) => (b.lastSeenAt ?? b.occurredAt) - (a.lastSeenAt ?? a.occurredAt),
  )
}

export async function clearFailures(): Promise<void> {
  memFailures.length = 0
  for (const l of memListeners) {
    try { l() } catch { /* ignore */ }
  }
  try {
    await db.failure_log.clear()
  } catch {
    /* DB unwritable — the in-memory log is already cleared */
  }
}

export async function countRecentFailures(sinceMs: number): Promise<number> {
  return db.failure_log.where('occurredAt').above(sinceMs).count()
}

// ─────────── auto-follow campaigns ───────────

export async function listCampaigns(
  accountDid?: string,
): Promise<AutoFollowCampaignRow[]> {
  const rows = accountDid
    ? await db.auto_follow_campaigns
        .where('accountDid')
        .equals(accountDid)
        .toArray()
    : await db.auto_follow_campaigns.toArray()
  return rows.sort((a, b) => b.createdAt - a.createdAt)
}

export async function upsertCampaign(row: AutoFollowCampaignRow): Promise<void> {
  await db.auto_follow_campaigns.put(row)
}

export async function getAutoFollowCampaign(
  id: string,
): Promise<AutoFollowCampaignRow | undefined> {
  return db.auto_follow_campaigns.get(id)
}

export async function deleteAutoFollowCampaign(id: string): Promise<void> {
  await db.auto_follow_campaigns.delete(id)
  // Cascade — drop every action this campaign queued so re-creating a
  // campaign with the same tags isn't blocked by stale dedupe entries.
  await deleteCampaignFollowActions(id).catch(() => undefined)
}

/** All running campaigns whose nextTickAt has elapsed (or is unset
 *  — first tick). Runner pulls this each scheduler tick. */
export async function listDueAutoFollowCampaigns(
  now: number,
): Promise<AutoFollowCampaignRow[]> {
  const rows = await db.auto_follow_campaigns
    .where('status')
    .equals('running')
    .toArray()
  return rows.filter((c) => (c.nextTickAt ?? 0) <= now)
}

/** DIDs of every follow action currently queued (pending/running)
 *  for this account — used to avoid double-queueing the same target. */
export async function pendingFollowDids(
  accountDid: string,
): Promise<Set<string>> {
  const rows = await db.scheduled_actions
    .where('[accountDid+status]')
    .anyOf([
      [accountDid, 'pending'],
      [accountDid, 'running'],
    ])
    .toArray()
  const out = new Set<string>()
  for (const r of rows) {
    if (r.kind === 'follow' && typeof r.subject === 'string') out.add(r.subject)
  }
  return out
}

/** DIDs we should NOT re-queue a follow for on this account. Includes:
 *   - DONE: we already follow them, bsky will reject a duplicate
 *   - PENDING / RUNNING: a follow is already queued / mid-flight
 *  But NOT failed or cancelled — those are retryable. Many "failed"
 *  rows in the wild are from the duplicate-follow bug fixed in
 *  v0.21.2, which means the actual follow may have landed and the
 *  retry will be a no-op, OR the failure was transient and the
 *  retry is a real second chance. Either way, blacklisting them
 *  forever blocked the campaign from making any progress. */
export async function excludedFollowDidsForAccount(
  accountDid: string,
): Promise<Set<string>> {
  const rows = await db.scheduled_actions
    .where('accountDid')
    .equals(accountDid)
    .toArray()
  const out = new Set<string>()
  for (const r of rows) {
    if (r.kind !== 'follow' || typeof r.subject !== 'string') continue
    if (r.status === 'done' || r.status === 'pending' || r.status === 'running') {
      out.add(r.subject)
    }
  }
  return out
}

/** Legacy alias kept temporarily so any in-flight caller doesn't
 *  break — both names resolve to the new (retry-friendly) behaviour. */
export const allFollowAttemptDidsForAccount = excludedFollowDidsForAccount

/** All follow actions for one specific campaign, used to reconcile
 *  succeeded/failed counters back into the campaign row. */
export async function listCampaignFollowActions(
  campaignId: string,
): Promise<ScheduledActionRow[]> {
  const rows = await db.scheduled_actions
    .where('campaignId')
    .equals(campaignId)
    .toArray()
  return rows.filter((r) => r.kind === 'follow')
}

/** Delete every scheduled action tagged with this campaign. Used by
 *  the campaign editor's "reset progress" button when the operator
 *  wants to wipe the slate without losing the campaign config. */
export async function deleteCampaignFollowActions(
  campaignId: string,
): Promise<number> {
  const rows = await listCampaignFollowActions(campaignId)
  await db.scheduled_actions.bulkDelete(rows.map((r) => r.id))
  return rows.length
}

// ─────────── horny_stats ───────────

export async function getHornyStats(
  handle: string,
): Promise<HornyStatsRow | undefined> {
  return db.horny_stats.get(handle)
}

export async function putHornyStats(row: HornyStatsRow): Promise<void> {
  await db.horny_stats.put(row)
}

// ─────────── ai_campaigns ───────────

export async function upsertAiCampaign(row: AiCampaignRow): Promise<void> {
  await db.ai_campaigns.put(row)
}

export async function listAiCampaigns(
  accountDid?: string,
): Promise<AiCampaignRow[]> {
  const rows = accountDid
    ? await db.ai_campaigns.where('accountDid').equals(accountDid).toArray()
    : await db.ai_campaigns.toArray()
  return rows.sort((a, b) => b.createdAt - a.createdAt)
}

export async function getAiCampaign(
  id: string,
): Promise<AiCampaignRow | undefined> {
  return db.ai_campaigns.get(id)
}

export async function deleteAiCampaign(id: string): Promise<void> {
  await db.ai_campaigns.delete(id)
  // Cascade — drop the run log too.
  const runs = await db.ai_campaign_runs.where('campaignId').equals(id).toArray()
  await db.ai_campaign_runs.bulkDelete(runs.map((r) => r.id))
}

export async function listDueAiCampaigns(now: number): Promise<AiCampaignRow[]> {
  return db.ai_campaigns
    .where('nextRunAt')
    .belowOrEqual(now)
    .filter((c) => !!c.enabled)
    .toArray()
}

export async function appendAiCampaignRun(run: AiCampaignRunRow): Promise<void> {
  await db.ai_campaign_runs.put(run)
}

export async function listAiCampaignRuns(
  campaignId: string,
  limit = 20,
): Promise<AiCampaignRunRow[]> {
  // [campaignId+startedAt] index: sort + limit happen inside IndexedDB.
  return db.ai_campaign_runs
    .where('[campaignId+startedAt]')
    .between([campaignId, -Infinity], [campaignId, Infinity])
    .reverse()
    .limit(limit)
    .toArray()
}

/** All post URIs ever evaluated by this campaign across all runs.
 *  (Historically a full-table filter scan over a stale-index worry; the
 *  campaignId index is exercised constantly by listAiCampaignRuns and
 *  IndexedDB re-indexes on schema upgrade, so the indexed query is safe.) */
export async function listSeenPostUris(campaignId: string): Promise<Set<string>> {
  const runs = await db.ai_campaign_runs
    .where('campaignId')
    .equals(campaignId)
    .toArray()
  const uris = new Set<string>()
  for (const run of runs) {
    for (const ev of run.evaluations) {
      uris.add(ev.postUri)
    }
  }
  return uris
}

/** Count how many liked/reposted actions each author handle received
 *  in the most recent `windowRuns` runs. Used to enforce per-handle
 *  diversity so the campaign doesn't repeatedly engage the same accounts. */
export async function listRecentHandleLikes(
  campaignId: string,
  windowRuns: number,
): Promise<Map<string, number>> {
  if (windowRuns <= 0) return new Map()
  const recent = await db.ai_campaign_runs
    .where('[campaignId+startedAt]')
    .between([campaignId, -Infinity], [campaignId, Infinity])
    .reverse()
    .limit(windowRuns)
    .toArray()
  const counts = new Map<string, number>()
  for (const run of recent) {
    for (const ev of run.evaluations) {
      if (ev.action === 'like' || ev.action === 'repost') {
        counts.set(ev.authorHandle, (counts.get(ev.authorHandle) ?? 0) + 1)
      }
    }
  }
  return counts
}

// ─────────── kv ───────────

export async function kvGet<T>(key: string): Promise<T | undefined> {
  try {
    return (await db.kv.get(key))?.value as T | undefined
  } catch {
    return undefined
  }
}

export async function kvSet(key: string, value: unknown): Promise<void> {
  await db.kv.put({ key, value })
}

export async function kvDelete(key: string): Promise<void> {
  try {
    await db.kv.delete(key)
  } catch {
    /* best-effort */
  }
}
